import { assertUnreachable } from "@repo/app-types/helpers/utilities";
import type {
  RecurringSchedule,
  ScheduleWeekday,
} from "@repo/app-types/scheduling/recurringSchedule";
import type { DateTime } from "luxon";

/**
 * When a schedule would next fire — the one client-side statement of the
 * calendar semantics, shared by admin (an agent's next run) and HQ (the KB
 * sync cadence preview). Client-side because both preview schedules the
 * reader is still editing; the scheduler only knows the saved one.
 *
 * Mirrors the cron the server derives from the same schedule
 * (`server-helpers/src/scheduling/scheduleRepeatOptions.ts`); keep the two in
 * step if the monthly variants change.
 */

/** luxon weekday numbering: Monday is 1. */
const weekdayToLuxonWeekday: Record<ScheduleWeekday, number> = {
  monday: 1,
  tuesday: 2,
  wednesday: 3,
  thursday: 4,
  friday: 5,
  saturday: 6,
  sunday: 7,
};

/** Covers the widest gap between two fires (a monthly schedule), with room. */
const maxDaysAhead = 400;

/** Whether a candidate day is one this schedule fires on. */
function firesOnDay(schedule: RecurringSchedule, day: DateTime): boolean {
  switch (schedule.frequency) {
    case "hourly":
    case "daily":
      return true;
    case "weekly":
      return schedule.weekdays.some(
        (weekday) => weekdayToLuxonWeekday[weekday] === day.weekday,
      );
    case "monthly": {
      const monthly = schedule.schedule;
      switch (monthly.kind) {
        case "first_day_of_month":
          return day.day === 1;
        case "first_weekday_of_month":
          // The month's first such weekday is necessarily in its first week.
          return (
            day.day <= 7 &&
            weekdayToLuxonWeekday[monthly.weekday] === day.weekday
          );
        case "last_day_of_month":
          return day.day === day.daysInMonth;
        default:
          return assertUnreachable(monthly);
      }
    }
    default:
      return assertUnreachable(schedule);
  }
}

/**
 * The next instant `schedule` fires after `now`, reading its `time_of_day` as
 * wall-clock time in `timeZone`. Null if that can't be resolved to a valid
 * instant (an unknown zone, a malformed time).
 */
export function nextScheduledRunAt(
  schedule: RecurringSchedule,
  timeZone: string,
  now: DateTime,
): DateTime | null {
  const from = now.setZone(timeZone);

  if (!from.isValid) {
    return null;
  }

  if (schedule.frequency === "hourly") {
    return from.startOf("hour").plus({ hours: 1 });
  }

  const [hourPart, minutePart] = schedule.time_of_day.split(":");
  const hour = Number(hourPart);
  const minute = Number(minutePart);

  // Checked up front because luxon's set() THROWS on NaN units rather than
  // going invalid — a malformed stored time must degrade, not crash a render.
  if (!Number.isInteger(hour) || !Number.isInteger(minute)) {
    return null;
  }

  // Walks days rather than jumping to the next match: one scan serves every
  // frequency, including the monthly variants whose next fire can be months out.
  for (let offset = 0; offset <= maxDaysAhead; offset += 1) {
    // startOf("day") before set(): plus({days}) carries `now`'s wall-clock
    // time onto the target day, and on the DST fall-back day a carried
    // afternoon time makes set() resolve an ambiguous 01:xx to the SECOND
    // (standard-time) occurrence — an hour after the cron actually fires.
    // From midnight, set() picks the first occurrence, matching cron.
    const candidate = from
      .plus({ days: offset })
      .startOf("day")
      .set({ hour, minute, second: 0, millisecond: 0 });

    if (!candidate.isValid) {
      return null;
    }

    if (candidate > from && firesOnDay(schedule, candidate)) {
      return candidate;
    }
  }

  return null;
}
